import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Minus, Plus, Star, Heart, Truck, Package, Moon, ChevronDown, Play, ShieldCheck, Home as HomeIcon } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Reveal } from "../components/motion";
import { HomeStorySections } from "../components/HomeSections";
import { useCart } from "../context/CartContext";

const BENEFITS = [
  { icon: Heart, label: '24" Oversized Cuddle Buddy' },
  { icon: Moon, label: "Softly Weighted Arms" },
  { icon: Star, label: "Stories & Soothing Sounds" },
  { icon: Star, label: "Parent-Controlled Glow" },
];

const PDP_FAQS = [
  ["What size is my Buddy?", "Every Buddy is approximately 24 inches long with an extra-long cuddle-pillow body — made for full-body hugs."],
  ["How are the arms weighted?", "The oversized arms are softly weighted so they rest naturally around your child. Final weight specifications will be published once product testing is complete."],
  ["How does the glow work?", "A soft, diffused LED glow sits in the belly. Parents choose the color and brightness in the Bedtime Buddies app."],
  ["How does the speaker work?", "Stories, music and sounds play through a built-in speaker, controlled from the parent app."],
  ["How does my Buddy connect to the app?", "Your Buddy pairs with the Bedtime Buddies app over Bluetooth in seconds, then remembers its bedtime settings."],
  ["Can I control brightness?", "Yes — from a candle-soft glow to a brighter reading light, all from the app."],
  ["Can I control volume?", "Yes, volume is fully adjustable in the parent app."],
  ["Is there a sleep timer?", "Yes. Set a timer and the lights and sounds gently wind down with the night."],
  ["How is my Buddy charged?", "With the included cable. Full charging and battery details ship with your Buddy."],
  ["How do I clean my Buddy?", "The plush outer is designed for surface cleaning with a damp cloth. Full care instructions ship with every Buddy."],
  ["What age is my Buddy designed for?", "Bedtime Buddies are comfort companions for preschool and elementary-age children. Final age guidance will be published once product testing is complete."],
];

export default function BuddyDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [buddy, setBuddy] = useState(null);
  const [all, setAll] = useState([]);
  const [qty, setQty] = useState(1);
  const [img, setImg] = useState(0);
  const swipeX = useRef(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    setBuddy(null); setImg(0); setQty(1);
    api.get(`/buddies/${slug}`).then((r) => setBuddy(r.data)).catch(() => setBuddy(false));
    api.get("/buddies", { params: { status: "launched" } }).then((r) => setAll(r.data)).catch(() => {});
  }, [slug]);

  if (buddy === null) return <div className="bg-cream py-32 text-center text-navy/40" data-testid="pdp-loading">Finding your Buddy…</div>;
  if (buddy === false) return (
    <div className="bg-cream py-32 text-center" data-testid="pdp-not-found">
      <p className="font-display text-3xl text-navy">This Buddy wandered off.</p>
      <Link to="/buddies" className="mt-6 inline-block rounded-full bg-pink px-7 py-3.5 font-body font-bold text-navy">Meet the others</Link>
    </div>
  );

  const comingSoon = buddy.availability === "coming_soon";

  const gallery = [
    { src: `/assets/studio/${buddy.slug}.png`, label: "Studio", alt: `${buddy.name} the ${buddy.animal} studio photo`, contain: true },
    { src: "/assets/hero.png", label: "Cuddles", alt: `Child cuddling ${buddy.name} in a softly lit bedroom` },
    { src: buddy.image, label: "Front", alt: `${buddy.name} the ${buddy.animal} front view`, contain: true },
    { src: "/assets/nightlight-art.png", label: "Night glow", alt: `${buddy.name} glowing softly at night` },
    { src: "/assets/hug.png", label: "Full body", alt: `${buddy.name} lying down, showing the extra-long cuddle body` },
    { src: buddy.image, label: "Texture", alt: `Close-up of ${buddy.name}'s ultra-soft plush`, zoom: true },
    { src: "/assets/final-cta.webp", label: "Video", alt: `${buddy.name} bedtime video`, video: true },
  ];
  const current = gallery[img];

  const onSwipeStart = (e) => { swipeX.current = e.touches?.[0]?.clientX ?? null; };
  const onSwipeEnd = (e) => {
    const endX = e.changedTouches?.[0]?.clientX;
    if (swipeX.current == null || endX == null) { swipeX.current = null; return; }
    const dx = endX - swipeX.current;
    swipeX.current = null;
    if (Math.abs(dx) > 40) setImg((i) => (i + (dx < 0 ? 1 : -1) + gallery.length) % gallery.length);
  };

  const buyNow = () => { add(buddy, qty); navigate("/checkout"); };

  return (
    <div data-testid="buddy-detail-page" className="overflow-x-clip bg-cream pb-24 lg:pb-0">
      {/* Breadcrumb */}
      <div className="border-b border-blush bg-lavender/50">
        <nav className="mx-auto max-w-7xl px-6 py-3 text-[13px] font-semibold text-navy/50" aria-label="Breadcrumb" data-testid="breadcrumb">
          <Link to="/" className="text-pinkdeep hover:underline">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/shop" className="hover:text-navy">Bedtime Buddies</Link>
          <span className="mx-2">/</span>
          <span className="text-navy/80">{buddy.name} the {buddy.animal}</span>
        </nav>
      </div>

      {/* Main product area */}
      <section className="mx-auto max-w-7xl gap-12 px-6 py-10 lg:grid lg:grid-cols-[55%_45%] lg:py-14">
        {/* Gallery */}
        <div>
          <div className="flex gap-4">
            <div className="hidden w-24 shrink-0 flex-col gap-3 sm:flex" role="tablist" aria-label="Product images">
              {gallery.map((g, i) => (
                <button key={i} onClick={() => setImg(i)} role="tab" aria-selected={img === i} aria-label={g.label} data-testid={`pdp-thumb-${i}`}
                  className={`relative h-24 w-24 overflow-hidden rounded-2xl transition ${img === i ? "ring-2 ring-pink ring-offset-2 ring-offset-cream" : "opacity-70 hover:opacity-100"}`}>
                  <img src={g.src} alt="" className={`h-full w-full object-cover ${g.zoom ? "scale-150" : ""}`} />
                  {g.video && <span className="absolute inset-0 flex items-center justify-center bg-navy/40"><Play size={22} className="text-white" fill="white" /></span>}
                </button>
              ))}
            </div>
            <div className="relative min-w-0 flex-1">
              <div className="touch-pan-y select-none overflow-hidden rounded-[2rem] shadow-[0_20px_60px_rgba(16,42,86,0.10)]"
                onTouchStart={onSwipeStart} onTouchEnd={onSwipeEnd} data-testid="pdp-swipe-area">
                <img src={current.src} alt={current.alt} data-testid="pdp-main-image"
                  className={`aspect-square w-full ${current.zoom ? "scale-150 object-cover" : current.contain ? "bg-[#FFF6EC] object-contain" : "object-cover"}`} />
                {current.video && <span className="absolute inset-0 flex items-center justify-center"><span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/85 shadow-xl"><Play size={26} className="ml-1 text-navy" fill="#102A56" /></span></span>}
              </div>
              <div className="mt-4 flex w-full max-w-full gap-3 overflow-x-auto p-1 no-scrollbar sm:hidden">
                {gallery.map((g, i) => (
                  <button key={i} onClick={() => setImg(i)} aria-label={g.label} data-testid={`pdp-thumb-m-${i}`}
                    className={`h-16 w-16 shrink-0 overflow-hidden rounded-xl transition ${img === i ? "ring-2 ring-pink ring-offset-2 ring-offset-cream" : "opacity-70"}`}>
                    <img src={g.src} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
              <p className="mt-6 flex items-center justify-center gap-2 text-center font-script text-2xl font-semibold text-navy sm:text-3xl" data-testid="pdp-handwritten">
                “More than a stuffed animal… a bedtime friend.”
                <Heart size={22} className="shrink-0 text-pink" />
              </p>
            </div>
          </div>
        </div>

        {/* Info column */}
        <div className="mt-10 lg:mt-0">
          <div className="flex items-center gap-2">
            <span className="flex gap-0.5 text-moon">{[...Array(5)].map((_, s) => <Star key={s} size={16} fill="currentColor" />)}</span>
            <span className="text-sm font-semibold text-navy/45" data-testid="pdp-review-count">Reviews coming soon</span>
          </div>
          <h1 className="mt-4 font-display text-4xl font-medium leading-[1.02] text-navy sm:text-5xl" data-testid="pdp-title">{buddy.name} the {buddy.animal}</h1>
          <p className="mt-1.5 text-xs font-bold uppercase tracking-[0.3em] text-pinkdeep">{buddy.personality}</p>
          <p className="mt-4 max-w-md text-base leading-relaxed text-navy/65">{buddy.short_desc}</p>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-body text-3xl font-extrabold text-navy" data-testid="pdp-price">${buddy.price.toFixed(2)} USD</span>
            {comingSoon && <span className="rounded-full bg-lavdeep px-3 py-1 font-body text-xs font-bold text-navy">COMING SOON</span>}
          </div>
          <p className="mt-1.5 text-sm text-navy/50">Taxes included. <span className="font-semibold text-pinkdeep">Shipping</span> calculated at checkout.</p>

          <div className="mt-7 grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
            {BENEFITS.map((b) => (
              <div key={b.label} className="flex items-start gap-2.5">
                <b.icon size={20} className="mt-0.5 shrink-0 text-pink" />
                <span className="text-[11px] font-bold uppercase leading-tight tracking-wide text-navy/75">{b.label}</span>
              </div>
            ))}
          </div>

          <div className="my-8 h-px bg-lavdeep/40" />

          <div className="flex items-end justify-between">
            <h2 className="font-display text-xl font-medium text-navy">Choose Your Buddy</h2>
            <span className="font-script text-xl font-semibold text-pinkdeep">Collect them all ♡</span>
          </div>
          <div className="mt-4 grid grid-cols-4 gap-4 sm:grid-cols-6" data-testid="buddy-selector">
            {all.map((b) => (
              <button key={b.slug} onClick={() => navigate(`/buddy/${b.slug}`)} data-testid={`select-${b.slug}`}
                className="group flex w-16 shrink-0 flex-col items-center gap-1.5" aria-label={`Choose ${b.name} the ${b.animal}`}>
                <span className={`h-16 w-16 overflow-hidden rounded-full bg-blush/60 transition ${b.slug === slug ? "ring-[2.5px] ring-pink ring-offset-2 ring-offset-cream shadow-[0_0_24px_rgba(245,140,200,0.5)]" : "group-hover:scale-105"}`}>
                  <img src={b.image} alt="" loading="lazy" className="h-full w-full object-cover" />
                </span>
                <span className="text-center text-[11px] font-bold leading-tight text-navy/70">{b.name}<br /><span className="font-semibold text-navy/45">the {b.animal}</span></span>
              </button>
            ))}
          </div>

          {!comingSoon && (
            <div className="mt-8">
              <p className="mb-3 font-body text-sm font-bold text-navy">Quantity</p>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex w-fit items-center gap-4 rounded-full border-2 border-blush bg-white px-5 py-3.5">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Decrease quantity" data-testid="pdp-qty-minus" className="text-pink transition hover:scale-125"><Minus size={17} strokeWidth={3} /></button>
                  <span className="w-5 text-center font-body text-lg font-bold text-navy" data-testid="pdp-qty">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} aria-label="Increase quantity" data-testid="pdp-qty-plus" className="text-pink transition hover:scale-125"><Plus size={17} strokeWidth={3} /></button>
                </div>
                <button onClick={() => { add(buddy, qty); toast.success(`${buddy.name} is in your cart!`); }} data-testid="pdp-add-to-cart"
                  className="flex-1 rounded-full bg-pink py-4.5 py-4 font-body text-lg font-bold text-navy shadow-[0_12px_30px_rgba(245,140,200,0.4)] transition hover:scale-[1.02] hover:bg-pinkdeep hover:text-white">
                  Add to Cart — ${(buddy.price * qty).toFixed(2)} USD
                </button>
              </div>
              <button onClick={buyNow} data-testid="pdp-buy-now"
                className="mt-3 w-full rounded-full border-2 border-navy bg-blush/50 py-3.5 font-body text-base font-bold text-navy transition hover:bg-blush">
                Buy Now
              </button>
              <div className="mt-7 grid grid-cols-3 gap-3">
                {[[Truck, "Free Shipping", "On orders over $75"], [Package, "Easy Returns", "30-night cuddle guarantee"], [Heart, "Made for Big Hugs", '24" oversized bedtime companion']].map(([Icon, t, s]) => (
                  <div key={t} className="flex items-start gap-2.5">
                    <Icon size={19} className="mt-0.5 shrink-0 text-navy/60" />
                    <div>
                      <p className="text-[12px] font-bold leading-tight text-navy">{t}</p>
                      <p className="mt-0.5 text-[11px] leading-tight text-navy/50">{s}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <HomeStorySections />

      {/* Family lifestyle gallery */}
      <section className="px-6 py-20 lg:py-24" data-testid="pdp-gallery-section">
        <Reveal className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-medium leading-[1.08] text-navy sm:text-4xl lg:text-5xl">Bedtime, in <span className="text-pink">real homes.</span></h2>
          <p className="mt-4 text-base leading-relaxed text-navy/60 sm:text-lg">A peek at {buddy.name} where it matters most — snuggled up at lights-out.</p>
        </Reveal>
        <div className="mx-auto mt-12 grid max-w-6xl grid-cols-2 gap-4 md:grid-cols-3">
          {["/assets/hero.png", "/assets/nightlight-art.png", "/assets/hug.png", "/assets/app-parent.png", "/assets/hero-mobile.png", "/assets/final-cta.webp"].map((src, i) => (
            <Reveal key={src} delay={i * 0.05}>
              <div className="overflow-hidden rounded-[1.75rem] shadow-[0_10px_36px_rgba(16,42,86,0.08)]" data-testid={"gallery-" + i}>
                <img src={src} alt={"Lifestyle photo " + (i + 1)} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-700 hover:scale-[1.04]" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Reviews placeholder */}
      <section className="bg-white px-6 py-20">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <span className="flex justify-center gap-1 text-moon/60">{[...Array(5)].map((_, s) => <Star key={s} size={22} />)}</span>
            <h2 className="mt-4 font-display text-3xl font-medium text-navy">Reviews</h2>
            <p className="mt-3 text-sm font-bold uppercase tracking-[0.25em] text-navy/35" data-testid="pdp-reviews-placeholder">Customer reviews coming soon</p>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-navy/50">
              Real reviews from real bedtime families will live here — with photos, star ratings and verified-buyer badges.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 30-night cuddle guarantee */}
      <section className="px-6 pt-4" data-testid="pdp-guarantee-section">
        <Reveal>
          <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 rounded-[2rem] border-2 border-moon/60 bg-white px-8 py-9 text-center shadow-[0_16px_50px_rgba(255,216,107,0.18)]">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-moon/25 text-navy"><ShieldCheck size={26} /></span>
            <h2 className="font-display text-2xl font-medium text-navy sm:text-3xl">30-night cuddle guarantee</h2>
            <p className="max-w-md text-[15px] leading-relaxed text-navy/60">Try {buddy.name} for 30 nights. If your Buddy does not become their favorite part of bedtime, send it back — no questions, no hard feelings.</p>
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-6 py-20">
        <Reveal>
          <h2 className="text-center font-display text-3xl font-medium text-navy">Questions about {buddy.name}?</h2>
        </Reveal>
        <div className="mt-10 divide-y divide-blush rounded-[2rem] bg-white px-7 shadow-[0_10px_40px_rgba(16,42,86,0.06)]" data-testid="pdp-faq">
          {PDP_FAQS.map(([q, a], i) => (
            <div key={q}>
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)} data-testid={`pdp-faq-q-${i}`} aria-expanded={openFaq === i}
                className="flex w-full items-center justify-between gap-4 py-4.5 py-4 text-left font-body text-[15px] font-bold text-navy transition hover:text-pinkdeep">
                {q}
                <ChevronDown size={18} className={`shrink-0 text-pink transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && <p className="pb-5 text-sm leading-relaxed text-navy/60" data-testid={`pdp-faq-a-${i}`}>{a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* Sticky mobile add to cart */}
      {!comingSoon && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-blush bg-white/95 p-3 shadow-[0_-8px_30px_rgba(16,42,86,0.08)] backdrop-blur-xl lg:hidden" data-testid="sticky-add-bar">
          <div className="flex items-center gap-3">
            <img src={buddy.image} alt="" className="h-12 w-10 rounded-xl object-cover" />
            <div className="flex-1">
              <p className="font-display text-sm font-medium text-navy">{buddy.name} the {buddy.animal}</p>
              <p className="font-body text-xs font-bold text-pinkdeep">${buddy.price.toFixed(2)}</p>
            </div>
            <button onClick={() => { add(buddy, qty); toast.success(`${buddy.name} is in your cart!`); }} data-testid="sticky-add-to-cart"
              className="rounded-full bg-pink px-6 py-3 font-body text-sm font-bold text-navy">
              Add to Cart
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
